import * as React from 'react';

import DateFnsUtils from '@date-io/date-fns';
import { Typography } from '@material-ui/core';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { addDays, differenceInDays } from 'date-fns';
import { connect } from 'react-redux';

import { Error } from 'components/error/Error';
import { Loader } from 'components/loader/Loader';

import { IChartConfigItem, IProps, IState } from './AdminDashboard.types';
import { GoogleAnalyticsChartsProvider, GoogleDataChart } from './charts';
import { getChartsConfigs } from './charts.config';
import './initAnalyticsScript';
import { fetchApiToken } from './redux';

import styles from './AdminDashBoard.module.styl';

const DEFAULT_DAY_RANGE = 7;
const ONE_YEAR_IN_DAYS = 365;

class AdminDashBoard extends React.Component<IProps, IState> {
  public state = {
    changeFrom: addDays(new Date(), -DEFAULT_DAY_RANGE),
    changeTo: new Date(),
    endDate: 0,
    isLoading: false,
    startDate: DEFAULT_DAY_RANGE,
    token: null,
    viewId: null,
  };

  public async componentDidMount(): Promise<void> {
    const result = await this.props.fetchApiToken();
    this.setState({
      isLoading: true,
    });
    const token =
      result && result.data && result.data.credentials
        ? result.data.credentials.access_token
        : null;
    const viewId = result && result.data ? result.data.viewId : null;

    if (token) {
      this.setState({
        isLoading: false,
        token,
        viewId,
      });
    }
  }

  public render() {
    const { changeFrom, changeTo } = this.state;

    return (
      <div className={styles.root}>
        <Typography className={styles.title} variant="h4">
          Google Analytics
        </Typography>
        <div className={styles.dateRange}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              inputVariant="outlined"
              value={changeFrom}
              onChange={this.handleChangeFrom}
              label="Start date"
              showTodayButton={true}
              format="dd/MM/yyyy"
              minDate={addDays(changeTo, -ONE_YEAR_IN_DAYS)}
              maxDate={changeTo}
            />
            <KeyboardDatePicker
              inputVariant="outlined"
              value={changeTo}
              onChange={this.handleChangeTo}
              label="End date"
              showTodayButton={true}
              format="dd/MM/yyyy"
              minDate={changeFrom}
              maxDate={Date.now()}
            />
          </MuiPickersUtilsProvider>
          <span className={styles.hint}>
            Date range should not be longer than one year
          </span>
        </div>
        {this.renderGoogleAnalyticsProvider()}
      </div>
    );
  }

  private readonly renderGoogleAnalyticsProvider = () => {
    const { viewId, token, isLoading, endDate, startDate } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    if (!token) {
      return <Error>Could not fetch token</Error>;
    }

    if (!viewId) {
      return <Error>Could not fetch viewId</Error>;
    }

    return (
      <GoogleAnalyticsChartsProvider accessToken={token}>
        <div className={styles.widgetsContainer}>
          {Object.entries(
            getChartsConfigs(`${startDate}daysAgo`, `${endDate}daysAgo`),
          ).map(([key, value]: [string, IChartConfigItem]) => (
            <GoogleDataChart key={key} viewId={viewId} config={value} />
          ))}
        </div>
      </GoogleAnalyticsChartsProvider>
    );
  };

  private handleChangeFrom = (value: Date | null) => {
    const { changeFrom, startDate, endDate } = this.state;

    if (value === null) {
      value = addDays(changeFrom, -DEFAULT_DAY_RANGE);
    }

    let startDateDiff = differenceInDays(Date.now(), value);

    if (startDateDiff - endDate > ONE_YEAR_IN_DAYS) {
      startDateDiff = endDate + ONE_YEAR_IN_DAYS;

      value = addDays(Date.now(), -startDateDiff);
    }

    this.setState({
      changeFrom: value,
      startDate: isNaN(startDateDiff) ? startDate : startDateDiff,
    });
  };

  private handleChangeTo = (value: Date | null) => {
    if (value === null) {
      value = (Date() as unknown) as Date;
    }

    const { changeFrom, startDate, endDate } = this.state;

    const endDateDiff = differenceInDays(Date.now(), value);
    let changeFromValue = changeFrom;
    let startDateDiff = startDate;

    if (!isNaN(endDateDiff) && startDate - endDateDiff > ONE_YEAR_IN_DAYS) {
      startDateDiff = endDateDiff + ONE_YEAR_IN_DAYS;

      changeFromValue = addDays(Date.now(), -startDateDiff);
    }

    this.setState({
      changeFrom: changeFromValue,
      changeTo: value,
      endDate: isNaN(endDateDiff) ? endDate : endDateDiff,
    });
  };
}

const mapDispatch = {
  fetchApiToken,
};

export default connect(null, mapDispatch)(AdminDashBoard);
